import type { TFunction } from '@tecma/i18n';

import { ENVIRONMENT } from 'constants/environment';

import type { HighlightedContentProps } from 'components/HighlightedContent/HighlightedContent';
import type { Area } from 'types/project';
import type { QuoteStatus } from 'types/quote';

type BannerProps = {
  [key: string]: HighlightedContentProps;
};

type RoomBannerProps = {
  [key in QuoteStatus]: HighlightedContentProps;
};

type GetBannerProps = {
  t: TFunction;
  firstButtonAction?: () => void;
  secondButtonAction?: () => void;
  thirdButtonAction?: () => void;
  sendRequestAction?: () => void;
  contactUsAction?: () => void;
  checkInDate?: string;
  area?: Area;
};

type GetRoomBannerProps = {
  t: TFunction;
  sendRequestAction?: () => void;
  moreDetailsAction?: () => void;
  contactUsAction?: () => void;
  createConfigurationAction?: () => void;
  checkInDate?: string;
};

export const getRoomBannerProps = ({
  t,
  sendRequestAction = () => {},
  moreDetailsAction = () => {},
  contactUsAction = () => {},
  createConfigurationAction = () => {},
  checkInDate,
}: GetRoomBannerProps): RoomBannerProps => {
  const sendRequestButton = {
    label: t('card-apartment.highlighted-content.button-label.send-request'),
    onClick: sendRequestAction,
  };
  const contactUsButtonOld = {
    label: t('card-apartment.highlighted-content.button-label.contact-us'),
    onClick: contactUsAction,
  };
  const moreDetailsButton = {
    label: t('card-apartment.highlighted-content.button-label.more-details'),
    onClick: moreDetailsAction,
  };
  const createConfigurationButton = {
    label: t('card-apartment.highlighted-content.button-label.add-configuration'),
    onClick: createConfigurationAction,
    iconName: 'plus',
  };
  const contactUsButton = {
    label: t('card-apartment.highlighted-content.contact-us'),
    onClick: contactUsAction,
  };
  return {
    suspended: {
      iconName: 'myhome-contact',
      title: t('card-apartment.highlighted-content.asset-type-room.suspended.title'),
      subtitle: t('card-apartment.highlighted-content.asset-type-room.suspended.subtitle'),
      firstButton: createConfigurationButton,
      secondButton: moreDetailsButton,
      contactUsButton,
      color: 'transparent',
    },
    expired: {
      iconName: 'myhome-expire',
      title: t('card-apartment.highlighted-content.asset-type-room.expired.title'),
      subtitle: t('card-apartment.highlighted-content.asset-type-room.expired.subtitle'),
      firstButton: createConfigurationButton,
      secondButton: moreDetailsButton,
      contactUsButton,
      color: 'transparent',
    },
    init: {
      iconName: 'myhome-contact',
      title: t('card-apartment.highlighted-content.asset-type-room.init.title'),
      subtitle: t('card-apartment.highlighted-content.asset-type-room.init.subtitle'),
      firstButton: ENVIRONMENT.REACT_APP_DISABLE_SEND_REQUEST_FLOW ? contactUsButtonOld : sendRequestButton,
      secondButton: moreDetailsButton,
    },
    invalid: {
      iconName: 'myhome-expire',
      title: t('card-apartment.highlighted-content.asset-type-room.invalid.title'),
      subtitle: t('card-apartment.highlighted-content.asset-type-room.invalid.subtitle'),
      firstButton: createConfigurationButton,
      secondButton: moreDetailsButton,
      contactUsButton,
      color: 'transparent',
    },
    applicationCompleted: {
      iconName: 'myhome-settle',
      title: t('card-apartment.highlighted-content.asset-type-room.applicationCompleted.title'),
      subtitle: t('card-apartment.highlighted-content.asset-type-room.applicationCompleted.subtitle'),
      secondButton: moreDetailsButton,
      contactUsButton,
      color: 'inverse',
    },
    accepted: {
      iconName: 'myhome-settle',
      title: t('card-apartment.highlighted-content.asset-type-room.accepted.title'),
      subtitle: t('card-apartment.highlighted-content.asset-type-room.accepted.subtitle', { checkIn: checkInDate }),
      secondButton: moreDetailsButton,
      contactUsButton,
      color: 'inverse',
    },
    rented: {
      iconName: 'myhome-contact',
      title: t('card-apartment.highlighted-content.asset-type-room.rented.title'),
      subtitle: t('card-apartment.highlighted-content.asset-type-room.rented.subtitle'),
      secondButton: moreDetailsButton,
      contactUsButton,
      color: 'inverse',
    },
    canceled: {
      iconName: 'myhome-expire',
      title: t('card-apartment.highlighted-content.asset-type-room.canceled.title'),
      subtitle: t('card-apartment.highlighted-content.asset-type-room.canceled.subtitle'),
      firstButton: createConfigurationButton,
      secondButton: moreDetailsButton,
      contactUsButton,
      color: 'transparent',
    },
    declined: {
      iconName: 'myhome-expire',
      title: t('card-apartment.highlighted-content.asset-type-room.declined.title'),
      subtitle: t('card-apartment.highlighted-content.asset-type-room.declined.subtitle'),
      firstButton: createConfigurationButton,
      secondButton: moreDetailsButton,
      contactUsButton,
      color: 'transparent',
    },
    rentCanceled: {
      iconName: 'myhome-contact',
      title: t('card-apartment.highlighted-content.asset-type-room.rentCanceled.title'),
      subtitle: t('card-apartment.highlighted-content.asset-type-room.rentCanceled.subtitle'),
      secondButton: moreDetailsButton,
      contactUsButton,
      color: 'transparent',
    },
    rentCompleted: {
      iconName: 'myhome-contact',
      title: t('card-apartment.highlighted-content.asset-type-room.rentCompleted.title'),
      subtitle: t('card-apartment.highlighted-content.asset-type-room.rentCompleted.subtitle'),
      secondButton: moreDetailsButton,
      contactUsButton,
      color: 'inverse',
    },
  };
};

export const getBannerProps = ({
  t,
  firstButtonAction = () => {},
  secondButtonAction = () => {},
  thirdButtonAction = () => {},
  sendRequestAction = () => {},
  contactUsAction = () => {},
  checkInDate,
  area,
}: GetBannerProps): BannerProps => {
  const sendRequestButton = {
    label: t('card-apartment.highlighted-content.button-label.send-request'),
    onClick: sendRequestAction,
  };
  const moreDetailsButton = {
    label: t('card-apartment.highlighted-content.button-label.more-details'),
    onClick: secondButtonAction,
  };
  const createConfigurationButton = {
    label: t('card-apartment.highlighted-content.button-label.add-configuration'),
    onClick: thirdButtonAction,
    iconName: 'plus',
  };
  const contactUsButton = {
    label: t('card-apartment.highlighted-content.contact-us'),
    onClick: contactUsAction,
  };
  const defaultFirstButton = {
    label: t('card-apartment.highlighted-content.button-label.contact-us'),
    onClick: firstButtonAction,
  };
  const defaultSecondButton = {
    label: t('card-apartment.highlighted-content.button-label.more-details'),
    onClick: secondButtonAction,
  };
  const negativeStatusFirstButton = {
    label:
      area === 'rent'
        ? t('card-apartment.highlighted-content.button-label.add-configuration')
        : t('card-apartment.highlighted-content.button-label.contact-us'),
    onClick: area === 'rent' ? thirdButtonAction : firstButtonAction,
    iconName: area === 'rent' ? 'plus' : '',
  };
  const negativeStatusSecondButton = {
    label: t('card-apartment.highlighted-content.button-label.more-details'),
    onClick: secondButtonAction,
  };
  return {
    interest: {
      iconName: 'myhome-settle',
      title: t('card-apartment.highlighted-content.interest.title'),
      subtitle: t('card-apartment.highlighted-content.interest.subtitle'),
      firstButton: {
        label:
          area === 'rent'
            ? t('card-apartment.highlighted-content.button-label.send-request')
            : t('card-apartment.highlighted-content.button-label.interested'),
        onClick: area === 'rent' ? sendRequestAction : firstButtonAction,
      },
      secondButton: defaultSecondButton,
    },
    reserved: {
      iconName: 'myhome-reserve',
      title: t('card-apartment.highlighted-content.reserved.title'),
      subtitle: t('card-apartment.highlighted-content.reserved.subtitle'),
      firstButton: defaultFirstButton,
      secondButton: defaultSecondButton,
    },
    proposal: {
      iconName: 'myhome-proposal',
      title: t('card-apartment.highlighted-content.proposal.title'),
      subtitle: t('card-apartment.highlighted-content.proposal.subtitle'),
      firstButton: defaultFirstButton,
      secondButton: defaultSecondButton,
      color: 'inverse',
    },
    settlement: {
      iconName: 'myhome-settle',
      title: t('card-apartment.highlighted-content.settlement.title'),
      subtitle: t('card-apartment.highlighted-content.settlement.subtitle'),
      firstButton: defaultFirstButton,
      secondButton: defaultSecondButton,
      color: 'inverse',
    },
    sold: {
      iconName: 'myhome-contact',
      title: t('card-apartment.highlighted-content.sold.title'),
      subtitle: t('card-apartment.highlighted-content.sold.subtitle'),
      firstButton: defaultFirstButton,
      secondButton: defaultSecondButton,
      color: 'inverse',
    },
    unavailable: {
      iconName: 'myhome-expire',
      title: t('card-apartment.highlighted-content.unavailable.title'),
      subtitle: t('card-apartment.highlighted-content.unavailable.subtitle'),
      firstButton: negativeStatusFirstButton,
      secondButton: negativeStatusSecondButton,
      color: 'transparent',
    },
    expired: {
      iconName: 'myhome-expire',
      title: t('card-apartment.highlighted-content.expired.title'),
      subtitle: t('card-apartment.highlighted-content.expired.subtitle'),
      firstButton: negativeStatusFirstButton,
      secondButton: negativeStatusSecondButton,
      color: 'transparent',
    },
    ...(area === 'rent'
      ? {
          suspended: {
            iconName: 'myhome-contact',
            title: t('card-apartment.highlighted-content.asset-type-room.suspended.title'),
            subtitle: t('card-apartment.highlighted-content.asset-type-room.suspended.subtitle'),
            firstButton: createConfigurationButton,
            secondButton: moreDetailsButton,
            contactUsButton,
            color: 'transparent',
          },
          init: {
            iconName: 'myhome-contact',
            title: t('card-apartment.highlighted-content.asset-type-room.init.title'),
            subtitle: t('card-apartment.highlighted-content.asset-type-room.init.subtitle'),
            firstButton: sendRequestButton,
            secondButton: moreDetailsButton,
          },
          invalid: {
            iconName: 'myhome-expire',
            title: t('card-apartment.highlighted-content.asset-type-room.invalid.title'),
            subtitle: t('card-apartment.highlighted-content.asset-type-room.invalid.subtitle'),
            firstButton: createConfigurationButton,
            secondButton: moreDetailsButton,
            contactUsButton,
            color: 'transparent',
          },
          applicationCompleted: {
            iconName: 'myhome-settle',
            title: t('card-apartment.highlighted-content.asset-type-room.applicationCompleted.title'),
            subtitle: t('card-apartment.highlighted-content.asset-type-room.applicationCompleted.subtitle'),
            secondButton: moreDetailsButton,
            contactUsButton,
            color: 'inverse',
          },
          accepted: {
            iconName: 'myhome-settle',
            title: t('card-apartment.highlighted-content.asset-type-room.accepted.title'),
            subtitle: t('card-apartment.highlighted-content.asset-type-room.accepted.subtitle', { checkIn: checkInDate }),
            secondButton: moreDetailsButton,
            contactUsButton,
            color: 'inverse',
          },
          rented: {
            iconName: 'myhome-contact',
            title: t('card-apartment.highlighted-content.asset-type-room.rented.title'),
            subtitle: t('card-apartment.highlighted-content.asset-type-room.rented.subtitle'),
            secondButton: moreDetailsButton,
            contactUsButton,
            color: 'inverse',
          },
          canceled: {
            iconName: 'myhome-expire',
            title: t('card-apartment.highlighted-content.asset-type-room.canceled.title'),
            subtitle: t('card-apartment.highlighted-content.asset-type-room.canceled.subtitle'),
            firstButton: createConfigurationButton,
            secondButton: moreDetailsButton,
            contactUsButton,
            color: 'transparent',
          },
          declined: {
            iconName: 'myhome-expire',
            title: t('card-apartment.highlighted-content.asset-type-room.declined.title'),
            subtitle: t('card-apartment.highlighted-content.asset-type-room.declined.subtitle'),
            firstButton: createConfigurationButton,
            secondButton: moreDetailsButton,
            contactUsButton,
            color: 'transparent',
          },
          rentCanceled: {
            iconName: 'myhome-contact',
            title: t('card-apartment.highlighted-content.asset-type-room.rentCanceled.title'),
            subtitle: t('card-apartment.highlighted-content.asset-type-room.rentCanceled.subtitle'),
            secondButton: moreDetailsButton,
            contactUsButton,
            color: 'transparent',
          },
          rentCompleted: {
            iconName: 'myhome-contact',
            title: t('card-apartment.highlighted-content.asset-type-room.rentCompleted.title'),
            subtitle: t('card-apartment.highlighted-content.asset-type-room.rentCompleted.subtitle'),
            secondButton: moreDetailsButton,
            contactUsButton,
            color: 'inverse',
          },
        }
      : {}),
  };
};
